"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";
import { cn } from "@/lib/utils";

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  /** @deprecated rsuite compat — ignored */
  inline?: boolean;
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ inline: _inline, className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn("flex flex-col gap-2.5", className)}
    {...props}
  />
));
RadioGroup.displayName = "RadioGroup";

export interface RadioGroupItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    "className" | "style"
  > {
  label?: React.ReactNode;
  description?: string;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ label, description, id, ...props }, ref) => {
  const generatedId = React.useId();
  const itemId = id ?? generatedId;

  return (
    <div className="flex shrink-0 items-start gap-3">
      <RadioGroupPrimitive.Item
        ref={ref}
        id={itemId}
        className={cn(
          "mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-input bg-white/70 transition-all duration-200 outline-none dark:bg-zinc-950/70",
          "hover:border-primary/70",
          "focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=checked]:border-primary data-[state=checked]:shadow-[0_0_0_3px_rgba(99,102,241,0.14)]"
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <span className="block h-2.5 w-2.5 rounded-full bg-primary" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>

      {(label ?? description) && (
        <div className="flex flex-col gap-0.5">
          {label && (
            <label
              htmlFor={itemId}
              className="cursor-pointer select-none text-sm font-medium text-foreground"
            >
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      )}
    </div>
  );
});
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
